/**
 * Thumbnails (preferred): Portunus proxies GET /pub/<asp>/<id>/thumbnail?document_id=&preset=
 * to CRAB with server-side X-Atollon-* headers (see portunus flask _crab_thumbnail_proxy).
 * ThumbnailImage uses that same-origin URL as <img src> — no session in public JSON.
 *
 * Fallback: client fetch to CRAB with headers when REACT_APP_ATOLLON_URL + session in JSON
 * (thumbnailContext / instance+session). Legacy: REACT_APP_THUMBNAIL_LEGACY=1, etc.
 *
 * Env:
 *   REACT_APP_ATOLLON_URL        — SOAP site base for client-side CRAB fallback
 *   REACT_APP_ATOLLON_SERVER     — optional default for X-Atollon-Server if instance missing
 *   REACT_APP_THUMBNAIL_BASE_URL — optional absolute API host for CRAB path
 *   REACT_APP_THUMBNAIL_CRAB_PATH — default /crab/thumbnail
 *   REACT_APP_THUMBNAIL_PRESET   — default gallery_thumb
 *   REACT_APP_THUMBNAIL_PATH     — legacy path template with {documentId}
 *   REACT_APP_THUMBNAIL_EXTRA_QUERY
 */

/** Placeholder until root session / thumbnail proxy works. Set '' or REACT_APP_TEMPORARY_THUMBNAIL_OFF=1 to disable. */
const TEMPORARY_THUMBNAIL_OVERRIDE_URL =
  typeof process !== 'undefined' &&
  process.env &&
  String(process.env.REACT_APP_TEMPORARY_THUMBNAIL_URL || '').trim() !== ''
    ? String(process.env.REACT_APP_TEMPORARY_THUMBNAIL_URL).trim()
    : 'https://gehealthcare-ultrasound.com/media/ultrasound/Products/Vivid/Vivid_E90/vivid_e90__1_.png';

/**
 * When non-null, ThumbnailImage uses this for every document thumb (ignores API URLs).
 * @returns {string|null}
 */
export function getTemporaryThumbnailOverride() {
  if (String(process.env.REACT_APP_TEMPORARY_THUMBNAIL_OFF || '') === '1') {
    return null;
  }
  const u = String(TEMPORARY_THUMBNAIL_OVERRIDE_URL || '').trim();
  return u || null;
}

/**
 * @param {Record<string, unknown>|null|undefined} payload
 * @returns {{ instance: string|null, session: string|null }}
 */
export function getThumbnailAuth(payload) {
  if (!payload || typeof payload !== 'object') {
    return { instance: null, session: null };
  }
  const tc = payload.thumbnailContext;
  if (tc && typeof tc === 'object') {
    const instance = tc.instance != null ? String(tc.instance) : null;
    const session = tc.session != null ? String(tc.session) : null;
    if (instance || session) return { instance, session };
  }
  const instance =
    payload.instance != null
      ? String(payload.instance)
      : payload.aspInstance != null
        ? String(payload.aspInstance)
        : null;
  const session =
    payload.session != null
      ? String(payload.session)
      : payload.rootSession != null
        ? String(payload.rootSession)
        : null;
  return { instance, session };
}

/**
 * Headers required by thumbnail-service (CRAB). Returns null if session or base URL missing.
 * @param {Record<string, unknown>|null|undefined} payload
 * @returns {Record<string, string>|null}
 */
export function getCrabThumbnailHeaders(payload) {
  const { instance, session } = getThumbnailAuth(payload);
  const rawUrl = String(process.env.REACT_APP_ATOLLON_URL || '').trim();
  if (!session || !rawUrl) return null;
  let endpoint = rawUrl.replace(/\/$/, '');
  if (!endpoint.startsWith('https://')) {
    endpoint = `https://${endpoint.replace(/^\/\//, '')}`;
  }
  const server =
    instance ||
    String(process.env.REACT_APP_ATOLLON_SERVER || '').trim() ||
    'default';
  return {
    'X-Atollon-Session': session,
    'X-Atollon-Server': server,
    'X-Atollon-Url': endpoint,
  };
}

/**
 * Same-origin Portunus thumbnail proxy (server adds CRAB auth). Uses current path /pub/...
 * @param {Record<string, unknown>} doc
 * @param {string} [preset]
 * @returns {string|null}
 */
export function buildProxiedThumbnailSrc(doc, preset) {
  if (!doc || typeof doc !== 'object' || doc.documentId == null) return null;
  if (typeof window === 'undefined' || !window.location?.pathname) return null;
  const pathname = window.location.pathname;
  const m = pathname.match(/^\/pub\/(.+?)\/?$/);
  if (!m) return null;
  const pubBase = `/pub/${m[1].replace(/\/$/, '')}`;
  const p =
    preset ||
    String(process.env.REACT_APP_THUMBNAIL_PRESET || 'gallery_thumb').trim();
  const qs = new URLSearchParams({
    document_id: String(doc.documentId),
    preset: p,
  });
  return `${pubBase}/thumbnail?${qs.toString()}`;
}

/**
 * CRAB thumbnail URL (query only; headers via getCrabThumbnailHeaders + fetch).
 * @param {Record<string, unknown>} doc
 * @param {string} [preset]
 * @returns {string|null}
 */
export function buildCrabThumbnailUrl(doc, preset) {
  if (!doc || typeof doc !== 'object' || doc.documentId == null) return null;
  const p =
    preset ||
    String(process.env.REACT_APP_THUMBNAIL_PRESET || 'gallery_thumb').trim();
  const path = String(
    process.env.REACT_APP_THUMBNAIL_CRAB_PATH || '/crab/thumbnail'
  );
  const qs = new URLSearchParams({
    preset: p,
    document_id: String(doc.documentId),
  });
  const rel = `${path.startsWith('/') ? path : `/${path}`}?${qs.toString()}`;
  const base = String(process.env.REACT_APP_THUMBNAIL_BASE_URL || '').replace(
    /\/$/,
    ''
  );
  if (base) return `${base}${rel}`;
  return rel;
}

/**
 * @param {Record<string, unknown>} doc — document row from payload.documents
 * @param {Record<string, unknown>} payload — full certificate JSON
 * @returns {string|null}
 */
export function buildDocumentThumbnailSrc(doc, payload) {
  if (!doc || typeof doc !== 'object') return null;

  if (typeof doc.thumbnailUrl === 'string' && doc.thumbnailUrl.trim() !== '') {
    return doc.thumbnailUrl.trim();
  }
  if (typeof doc.url === 'string' && doc.url.trim() !== '') {
    return doc.url.trim();
  }
  if (typeof doc.publicUrl === 'string' && doc.publicUrl.trim() !== '') {
    return doc.publicUrl.trim();
  }

  if (doc.documentId == null) return null;

  if (String(process.env.REACT_APP_THUMBNAIL_LEGACY || '') === '1') {
    return buildDocumentThumbnailSrcLegacy(doc, payload);
  }

  return null;
}

function buildDocumentThumbnailSrcLegacy(doc, payload) {

  const base = String(process.env.REACT_APP_THUMBNAIL_BASE_URL || '').replace(/\/$/, '');
  const pathTemplate =
    process.env.REACT_APP_THUMBNAIL_PATH || '/svc/thumbnail/{documentId}';
  const path = pathTemplate.replace(
    /\{documentId\}/g,
    encodeURIComponent(String(doc.documentId))
  );

  const q = new URLSearchParams();
  const { instance, session } = getThumbnailAuth(payload);
  if (instance) q.set('instance', instance);
  if (session) q.set('session', session);

  const extra = process.env.REACT_APP_THUMBNAIL_EXTRA_QUERY || '';
  if (extra.trim()) {
    for (const part of extra.split('&')) {
      const eq = part.indexOf('=');
      if (eq === -1) {
        if (part) q.set(part, '');
      } else {
        const k = part.slice(0, eq);
        const v = part.slice(eq + 1);
        if (k) q.set(k, v);
      }
    }
  }

  const qs = q.toString();
  const rel = `${path}${qs ? `?${qs}` : ''}`;
  if (base) return `${base}${rel.startsWith('/') ? rel : `/${rel}`}`;
  return rel.startsWith('/') ? rel : `/${rel}`;
}
